import { useNavigate, useParams } from "react-router-dom";
import { FileText, Film, Presentation, Users2, Layers, ClipboardCheck, MessageCircleQuestion, Clock } from "lucide-react";

const EVIDENCE_ICONS = {
  session: Users2, rehearsal: Film, interaction_attempt: Film, assignment: FileText,
  presentation_project: Presentation, section: Presentation, presence_context: Presentation,
  file_asset: FileText, layer: Layers,
};

const STATUS_BADGE = {
  draft: { label: "Draft", className: "bg-ink/10 text-ink-soft" },
  published: { label: "Published", className: "bg-sage/20 text-sage-deep" },
  open: { label: "Awaiting coach", className: "bg-amber-100 text-amber-800" },
  fulfilled: { label: "Fulfilled", className: "bg-sage/20 text-sage-deep" },
  cancelled: { label: "Cancelled", className: "bg-ink/10 text-ink-soft" },
};

/**
 * Master v5 Prompt 12 — one row in the Feedback archive. `kind="feedback"`
 * renders a Feedback row (draft or published); `kind="request"` renders
 * an open/fulfilled/cancelled FeedbackRequest. Both link through to the
 * detail page — every action (publish, respond, room launches) lives
 * there, never on this compact card.
 */
export default function FeedbackCard({ kind, item }) {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const evidenceRefs = kind === "feedback" ? item.evidence : [item.evidence];
  const badge = STATUS_BADGE[item.status] || STATUS_BADGE.draft;
  const isUnread = kind === "feedback" && item.status === "published" && item.is_read === false;

  return (
    <button
      onClick={() => navigate(kind === "feedback" ? `/rehearsal-room/w/${workspaceId}/feedback/${item.fbk_id}` : `/rehearsal-room/w/${workspaceId}/feedback/request/${item.frq_id}`)}
      className="w-full text-left rounded-xl border border-ink/10 bg-white/70 hover:border-terracotta/40 hover:shadow-sm transition-all p-4 space-y-2"
      data-testid={`feedback-card-${item.fbk_id || item.frq_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {isUnread && <span className="w-2 h-2 rounded-full bg-terracotta shrink-0" data-testid={`feedback-card-unread-dot-${item.fbk_id}`} />}
          {kind === "feedback" ? <ClipboardCheck size={16} className="text-terracotta shrink-0" /> : <MessageCircleQuestion size={16} className="text-terracotta shrink-0" />}
          <p className={`truncate ${isUnread ? "font-semibold text-ink" : "font-medium text-ink"}`}>{kind === "feedback" ? item.focus : item.question_text}</p>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${badge.className}`}>{badge.label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {evidenceRefs.map((ev, idx) => {
          const Icon = EVIDENCE_ICONS[ev.target_type] || FileText;
          return (
            <span key={idx} className="inline-flex items-center gap-1 text-xs text-ink-soft bg-ink/5 px-2 py-0.5 rounded-full">
              <Icon size={11} /> {ev.label || ev.target_type}
            </span>
          );
        })}
      </div>
      {kind === "feedback" && item.status === "published" && (
        <p className="text-xs text-ink-soft flex items-center gap-1"><Clock size={11} /> Published {new Date(item.published_at).toLocaleDateString()}</p>
      )}
    </button>
  );
}
